import axios from 'axios';
import Cookies from 'js-cookie';

// Configuración de la URL base de la API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // En producción, usar rutas relativas (mismo host)
  : 'http://localhost:8000'; // En desarrollo, usar la URL completa del backend

// Configurar axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a las requests
api.interceptors.request.use((config) => {
  const token = Cookies.get('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Si es error 401 y NO es una petición de login, redirigir
    // (si es login, dejamos que el componente maneje el error)
    if (error.response?.status === 401 && !error.config.url.includes('/auth/login')) {
      Cookies.remove('auth_token');
      // Redirigir al login según el entorno
      if (process.env.NODE_ENV === 'production') {
        window.location.href = '/public/login';
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    return response.data;
  },

  logout: async () => {
    await api.post('/api/auth/logout');
    Cookies.remove('auth_token');
  },

  me: async () => {
    const response = await api.get('/api/auth/me');
    // Manejar tanto el formato estandar {data: ...} como el formato directo {user: ...}
    return response.data.data || response.data;
  },
};

export const userService = {
  getProfile: async () => {
    const response = await api.get('/api/clients/me');
    return response.data.data;
  },

  getBalance: async () => {
    const response = await api.get('/api/credits/balance');
    return response.data.data;
  },

  // Paquetes de créditos (listar y compra)
  getCreditPackages: async () => {
    const response = await api.get('/api/credits/packages');
    return response.data.data;
  },

  purchaseCreditsPackage: async (packageId) => {
    const response = await api.post('/api/credits/purchase', { package_id: packageId });
    return response.data.data;
  },

  // Planes disponibles y cambio de plan
  getAvailablePlans: async () => {
    const response = await api.get('/api/plans');
    return response.data.data;
  },

  upgradePlan: async (planId) => {
    const response = await api.post('/api/plans/upgrade', { plan_id: planId });
    return response.data.data;
  },

  // Precios a granel
  getBulkPricing: async () => {
    const response = await api.get('/api/credits/bulk/pricing');
    return response.data.data;
  },

  getBulkQuote: async (quantity) => {
    const response = await api.get(`/api/credits/bulk/quote?quantity=${quantity}`);
    return response.data.data;
  },

  purchaseBulkCredits: async (quantity) => {
    const response = await api.post('/api/credits/bulk/purchase', { quantity });
    return response.data.data;
  },

  // Nuevo: generar link de pago para plan o compra a granel
  generatePaymentLink: async (payload) => {
    // payload: { email, plan_id } o { email, quantity }
    const response = await api.post('/api/payments/link/generate', payload);
    return response.data.data;
  },

  // Facturas y nóminas
  getInvoices: async (page = 1, skipDoc = null, filters = {}) => {
    const qs = new URLSearchParams();
    // Default pagination values if not provided via page/filters
    const limit = filters.limit || 10;
    const offset = (page - 1) * limit;

    qs.set('limit', limit);
    qs.set('offset', offset);

    if (skipDoc) qs.set('skipdoc', skipDoc);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    if (filters.status) qs.set('status', filters.status);
    if (filters.company_id) qs.set('company_id', filters.company_id);
    if (filters.date_from) qs.set('date_from', filters.date_from);
    if (filters.date_to) qs.set('date_to', filters.date_to);
    if (filters.metodo_pago) qs.set('metodo_pago', filters.metodo_pago);
    if (filters.receiver_id) qs.set('receiver_id', filters.receiver_id);
    const response = await api.get(`/api/invoices?${qs.toString()}`);
    return response.data;
  },

  getNominas: async (page = 1, skipDoc = null, filters = {}) => {
    const qs = new URLSearchParams();
    if (page != null) qs.set('page', page);
    const limit = filters.limit || 10;
    const offset = (page - 1) * limit;
    qs.set('limit', limit);
    qs.set('offset', offset);
    qs.set('tipo_comprobante', 'N');
    if (skipDoc) qs.set('skipdoc', skipDoc);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    if (filters.status) qs.set('status', filters.status);
    if (filters.company_id) qs.set('company_id', filters.company_id);
    if (filters.date_from) qs.set('date_from', filters.date_from);
    if (filters.date_to) qs.set('date_to', filters.date_to);
    const response = await api.get(`/api/invoices?${qs.toString()}`);
    return response.data;
  },

  createInvoice: async (data) => {
    const response = await api.post('/api/invoices', data);
    return response.data;
  },

  updateInvoice: async (id, data) => {
    const response = await api.put(`/api/invoices/${id}`, data);
    return response.data;
  },

  deleteInvoice: async (id) => {
    const response = await api.delete(`/api/invoices/${id}`);
    return response.data;
  },

  stampInvoice: async (id) => {
    const response = await api.post(`/api/invoices/${id}/stamp`);
    return response.data;
  },

  getInvoice: async (id) => {
    const response = await api.get(`/api/invoices/${id}`);
    return response.data;
  },

  // Crear pago en borrador
  createPayment: async (data) => {
    const response = await api.post('/api/payments', data);
    return response.data;
  },

  updatePayment: async (id, data) => {
    const response = await api.put(`/api/payments/${id}`, data);
    return response.data;
  },
  
  // Timbrar pago existentedel usuario
  getPayments: async (limit = 100, offset = 0, filters = {}) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', limit);
    if (offset != null) qs.set('offset', offset);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    if (filters.date_from) qs.set('date_from', filters.date_from);
    if (filters.date_to) qs.set('date_to', filters.date_to);
    if (filters.invoice_id) qs.set('invoice_id', filters.invoice_id);
    const response = await api.get(`/api/payments?${qs.toString()}`);
    return response.data;
  },

  // Timbrar pago existente
  stampPayment: async (id) => {
    const response = await api.post(`/api/payments/${id}/stamp`);
    return response.data;
  },

  // Eliminar pago
  deletePayment: async (id) => {
    const response = await api.delete(`/api/payments/${id}`);
    return response.data;
  },
  
  downloadInvoiceXml: async (id) => {
    return await api.get(`/api/invoices/${id}/xml`, { responseType: 'arraybuffer' });
  },

  generateInvoicePdf: async (id, options = {}) => {
    const resp = await api.post(`/api/invoices/${id}/pdf`, options);
    return resp.data;
  },
  
  previewInvoicePdf: async (id, options = {}) => {
    // Si se pasa un objeto en 'id' en lugar de un string, usamos el endpoint raw
    if (typeof id === 'object') {
        const resp = await api.post('/api/invoices/preview-raw', {
            invoice_data: id,
            ...options
        });
        return resp.data;
    }
    const resp = await api.post(`/api/invoices/${id}/pdf/preview`, options);
    return resp.data;
  },

  generatePaymentsPdf: async (id, options = {}) => {
    const resp = await api.post(`/api/payments/${id}/pdf`, options);
    return resp.data;
  },
  downloadPaymentsXml: async (id) => {
    return await api.get(`/api/payments/${id}/xml`, { responseType: 'arraybuffer' });
  },

  getCancellationMotives: async () => {
    const resp = await api.get('/api/cancellation/motives');
    return resp.data;
  },

  checkInvoiceCancellation: async (id) => {
    const resp = await api.get(`/api/invoices/${id}/cancellation/check`);
    return resp.data;
  },

  cancelInvoice: async (id, payload) => {
    const resp = await api.post(`/api/invoices/${id}/cancel`, payload);
    return resp.data;
  },

  getCancellationStatus: async (id) => {
    const resp = await api.get(`/api/invoices/${id}/cancellation/status`);
    return resp.data;
  },

  checkPaymentCancellation: async (id) => {
    const resp = await api.get(`/api/payments/${id}/cancellation/check`);
    return resp.data;
  },

  cancelPayment: async (id, payload) => {
    const resp = await api.post(`/api/payments/${id}/cancel`, payload);
    return resp.data;
  },

  getPaymentCancellationStatus: async (id) => {
    const resp = await api.get(`/api/payments/${id}/cancellation/status`);
    return resp.data;
  },

  sendInvoiceEmail: async (id, payload) => {
    const resp = await api.post(`/api/invoices/${id}/email`, payload);
    return resp.data;
  },

  getInvoiceTaxCalculation: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date_from) {
      params.set('date_from', filters.date_from);
    }
    if (filters.date_to) {
      params.set('date_to', filters.date_to);
    }
    if (filters.company_id) {
      params.set('company_id', filters.company_id);
    }
    const queryString = params.toString();
    const url = `/api/invoices/tax-calculation${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data.data;
  },

  syncSatDocuments: async (companyId, filters = {}) => {
    const payload = {};
    if (filters.date_from) payload.date_from = filters.date_from;
    if (filters.date_to) payload.date_to = filters.date_to;
    const response = await api.post(`/api/companies/${companyId}/sat-sync`, payload);
    return response.data;
  },

  getCobranzaPpdInvoices: async (limit = 20, offset = 0, filters = {}) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', limit);
    if (offset != null) qs.set('offset', offset);
    if (filters.company_id) qs.set('company_id', filters.company_id);
    if (filters.receiver_id) qs.set('receiver_id', filters.receiver_id);
    if (filters.uuid) qs.set('uuid', filters.uuid);
    const response = await api.get(`/api/collections/ppd/invoices?${qs.toString()}`);
    return response.data;
  },

  stampBulkPpdPayments: async (payload) => {
    const response = await api.post('/api/collections/ppd/payments/stamp-bulk', payload);
    return response.data;
  },

  getCobranzaPpdBatches: async (limit = 10, offset = 0) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', limit);
    if (offset != null) qs.set('offset', offset);
    const response = await api.get(`/api/collections/ppd/batches?${qs.toString()}`);
    return response.data;
  },
};

export const adminService = {
  // Usuarios
  getUsers: async (page = 1, search = '') => {
    const response = await api.get(`/api/admin/users?page=${page}&search=${search}`);
    return response.data;
  },

  getUser: async (id) => {
    const response = await api.get(`/api/admin/users/${id}`);
    return response.data.data;
  },

  getRecentUsers: async (limit = 10) => {
    const response = await api.get(`/api/admin/users/recent?limit=${limit}`);
    return response.data.data;
  },

  createUser: async (data) => {
    const response = await api.post('/api/admin/users', data);
    return response.data.data;
  },

  updateUser: async (id, data) => {
    const response = await api.put(`/api/admin/users/${id}`, data);
    return response.data.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/api/admin/users/${id}`);
    return response.data;
  },

  toggleUserStatus: async (id, isActive) => {
    const response = await api.patch(`/api/admin/users/${id}/status`, { is_active: isActive });
    return response.data.data;
  },

  // Gestión de planes de usuarios
  changeUserPlan: async (id, planId) => {
    await api.post(`/api/admin/users/${id}/change-plan`, { plan_id: planId });
  },

  addBulkCredits: async (id, credits) => {
    await api.post(`/api/admin/users/${id}/credits`, { credits });
  },

  addCredits: async (id, credits) => {
    await api.post(`/api/admin/users/${id}/credits`, { credits });
  },

  // Planes
  getPlans: async () => {
    const response = await api.get('/api/admin/plans');
    return response.data.data;
  },

  getPlan: async (id) => {
    const response = await api.get(`/api/admin/plans/${id}`);
    return response.data.data;
  },

  createPlan: async (data) => {
    const response = await api.post('/api/admin/plans', data);
    return response.data.data;
  },

  updatePlan: async (id, data) => {
    const response = await api.put(`/api/admin/plans/${id}`, data);
    return response.data.data;
  },

  deletePlan: async (id) => {
    const response = await api.delete(`/api/admin/plans/${id}`);
    return response.data;
  },

  // Estadísticas
  getStats: async () => {
    const response = await api.get('/api/admin/stats');
    return response.data.data;
  },

  getMonthlyStats: async () => {
    const response = await api.get('/api/admin/stats/monthly');
    return response.data.data;
  },

  getTopUsers: async (limit = 10) => {
    const response = await api.get(`/api/admin/stats/top-users?limit=${limit}`);
    return response.data.data;
  },
  regenerateInvoiceTaxes: async () => {
    const response = await api.post('/api/admin/invoices/regenerate-taxes');
    return response.data.data;
  },
  regenerateInvoiceTaxesForUser: async (id) => {
    const response = await api.post(`/api/admin/users/${id}/invoices/regenerate-taxes`);
    return response.data.data;
  },

  uploadCaptureLine: async (formData) => {
    const response = await api.post('/api/admin/capture-lines', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const productsService = {
  getProducts: async (search = '') => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/api/productos-servicios${params}`);
    return response.data;
  },

  getProduct: async (id) => {
    const response = await api.get(`/api/productos-servicios/${id}`);
    return response.data.data;
  },

  createProduct: async (data) => {
    const response = await api.post('/api/productos-servicios', data);
    return response.data.data;
  },

  updateProduct: async (id, data) => {
    const response = await api.put(`/api/productos-servicios/${id}`, data);
    return response.data.data;
  },

  deleteProduct: async (id) => {
    const response = await api.delete(`/api/productos-servicios/${id}`);
    return response.data;
  },

  searchProducts: async (search, limit = 20) => {
    const response = await api.get(`/api/productos-servicios/search?q=${encodeURIComponent(search)}&limit=${limit}`);
    return response.data;
  },

  importProductsCsv: async (formData) => {
    const response = await api.post('/api/productos-servicios/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const receiversService = {
  getReceivers: async (search = '', options = {}) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (options.includeEmployees) qs.set('include_employees', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/api/receivers${suffix}`);
    return response.data;
  },

  getReceiver: async (id) => {
    const response = await api.get(`/api/receivers/${id}`);
    return response.data.data;
  },

  createReceiver: async (data) => {
    const response = await api.post('/api/receivers', data);
    return response.data.data;
  },

  updateReceiver: async (id, data) => {
    const response = await api.put(`/api/receivers/${id}`, data);
    return response.data.data;
  },

  deleteReceiver: async (id) => {
    const response = await api.delete(`/api/receivers/${id}`);
    return response.data;
  },
};

export const employeesService = {
  getEmployees: async (search = '', companyId = null) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (companyId) qs.set('company_id', companyId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/api/employees${suffix}`);
    return response.data;
  },

  getEmployee: async (id) => {
    const response = await api.get(`/api/employees/${id}`);
    return response.data.data;
  },

  createEmployee: async (data) => {
    const response = await api.post('/api/employees', data);
    return response.data.data;
  },

  updateEmployee: async (id, data) => {
    const response = await api.put(`/api/employees/${id}`, data);
    return response.data.data;
  },

  deleteEmployee: async (id) => {
    const response = await api.delete(`/api/employees/${id}`);
    return response.data;
  },
};

export const catalogService = {
  searchProducts: async (search, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    if (limit) params.append('limit', limit);
    
    const response = await api.get(`/api/catalogs/productos?${params.toString()}`);
    return response.data;
  },

  searchUnits: async (search, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    if (limit) params.append('limit', limit);
    
    const response = await api.get(`/api/catalogs/unidades?${params.toString()}`);
    return response.data;
  },

  getProduct: async (id) => {
    const response = await api.get(`/api/catalogs/productos/${id}`);
    return response.data;
  },

  getUnit: async (id) => {
    const response = await api.get(`/api/catalogs/unidades/${id}`);
    return response.data;
  },

  validateProduct: async (id) => {
    const response = await api.post('/api/catalogs/productos/validate', { id });
    return response.data;
  },

  validateUnit: async (id) => {
    const response = await api.post('/api/catalogs/unidades/validate', { id });
    return response.data;
  },
};

export const folioSerieService = {
  getFolioSeries: async (companyId, filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    
    const response = await api.get(`/api/companies/${companyId}/folio-series?${params.toString()}`);
    return response.data;
  },

  getFolioSerie: async (companyId, id) => {
    const response = await api.get(`/api/companies/${companyId}/folio-series/${id}`);
    return response.data;
  },

  createFolioSerie: async (companyId, data) => {
    const response = await api.post(`/api/companies/${companyId}/folio-series`, data);
    return response.data;
  },

  updateFolioSerie: async (companyId, id, data) => {
    const response = await api.put(`/api/companies/${companyId}/folio-series/${id}`, data);
    return response.data;
  },

  deleteFolioSerie: async (companyId, id) => {
    const response = await api.delete(`/api/companies/${companyId}/folio-series/${id}`);
    return response.data;
  },

  toggleStatus: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/folio-series/${id}/toggle-status`);
    return response.data;
  },

  setAsDefault: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/folio-series/${id}/set-default`);
    return response.data;
  },
};

export const companiesService = {
  getCompanies: async () => {
    const response = await api.get('/api/companies');
    return response.data;
  },

  getCompany: async (id) => {
    const response = await api.get(`/api/companies/${id}`);
    return response.data;
  },

  createCompany: async (data) => {
    const response = await api.post('/api/companies', data);
    return response.data;
  },

  updateCompany: async (id, data) => {
    const response = await api.put(`/api/companies/${id}`, data);
    return response.data;
  },

  deleteCompany: async (id) => {
    const response = await api.delete(`/api/companies/${id}`);
    return response.data;
  },
};

export const businessActivitiesService = {
  getBusinessActivities: async () => {
    const response = await api.get('/api/business-activities');
    return response.data;
  },
};

export const autofacturaConfigsService = {
  getConfigs: async () => {
    const response = await api.get('/api/autofactura-configs');
    return response.data;
  },

  createConfig: async (data) => {
    const response = await api.post('/api/autofactura-configs', data);
    return response.data;
  },

  updateConfig: async (id, data) => {
    const response = await api.put(`/api/autofactura-configs/${id}`, data);
    return response.data;
  },

  deleteConfig: async (id) => {
    const response = await api.delete(`/api/autofactura-configs/${id}`);
    return response.data;
  },

  regenerateToken: async (id) => {
    const response = await api.post(`/api/autofactura-configs/${id}/regenerate-token`);
    return response.data;
  },

  sendLinkEmail: async (token) => {
    const response = await api.post(`/autofactura/${encodeURIComponent(token)}/send-link`);
    return response.data;
  },
};

export const certificatesService = {
  getCertificates: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/certificates`);
    return response.data;
  },

  getCertificate: async (companyId, id) => {
    const response = await api.get(`/api/companies/${companyId}/certificates/${id}`);
    return response.data;
  },

  uploadCertificate: async (companyId, formData) => {
    const response = await api.post(`/api/companies/${companyId}/certificates`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteCertificate: async (companyId, id) => {
    const response = await api.delete(`/api/companies/${companyId}/certificates/${id}`);
    return response.data;
  },

  toggleStatus: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/certificates/${id}/toggle-status`);
    return response.data;
  },
};

export const efirmaService = {
  getEfirma: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/efirma`);
    return response.data;
  },

  uploadEfirma: async (companyId, formData) => {
    const response = await api.post(`/api/companies/${companyId}/efirma`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteEfirma: async (companyId) => {
    const response = await api.delete(`/api/companies/${companyId}/efirma`);
    return response.data;
  },
};

export const captureLinesService = {
  listByCompany: async (companyId) => {
    const response = await api.get(`/api/companies/${companyId}/capture-lines`);
    return response.data;
  },
  getByPeriod: async (companyId, period) => {
    const response = await api.get(`/api/companies/${companyId}/capture-lines/period/${encodeURIComponent(period)}`);
    return response.data;
  },
  downloadByPeriod: async (companyId, period) => {
    const response = await api.get(`/api/companies/${companyId}/capture-lines/period/${encodeURIComponent(period)}/download`, {
      responseType: 'blob',
    });
    return response;
  },
};

export const companyTaxService = {
  getTaxes: async (companyId, filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined) {
        params.append(key, filters[key]);
      }
    });
    const queryString = params.toString();
    const url = `/api/companies/${companyId}/taxes${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  getTax: async (companyId, id) => {
    const response = await api.get(`/api/companies/${companyId}/taxes/${id}`);
    return response.data;
  },

  createTax: async (companyId, data) => {
    const response = await api.post(`/api/companies/${companyId}/taxes`, data);
    return response.data;
  },

  updateTax: async (companyId, id, data) => {
    const response = await api.put(`/api/companies/${companyId}/taxes/${id}`, data);
    return response.data;
  },

  deleteTax: async (companyId, id) => {
    const response = await api.delete(`/api/companies/${companyId}/taxes/${id}`);
    return response.data;
  },

  toggleStatus: async (companyId, id) => {
    const response = await api.patch(`/api/companies/${companyId}/taxes/${id}/toggle-status`);
    return response.data;
  },

  getCatalogs: async () => {
    const response = await api.get('/api/tax-catalogs');
    return response.data;
  },
};

export { default as taxTemplateService } from './taxTemplateService';
export default api;
